import type { RefObject } from 'react';
import { AlertTriangle, ShieldAlert, Siren as RescueIcon } from 'lucide-react';
import { GSI_BUTTON_WIDTH } from '../lib/auth';

/**
 * Google's G, inlined.
 *
 * Their branding guidelines require the real four colour mark on a custom sign in
 * button, and this is that mark. Inline rather than an <img> so it cannot fail to
 * load on the one screen that has to work during an outage.
 */
function GoogleMark() {
  return (
    <svg className="size-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export interface LoginScreenProps {
  /** Mount point for the Google button, owned by useGoogleAuth. */
  buttonRef: RefObject<HTMLDivElement>;
  /** True while the Google script is still loading. */
  loading: boolean;
  /** Set when sign in cannot work at all: bad build config, or the script never loaded. */
  blockedReason: string | null;
  /** Why the previous session ended, e.g. it expired. */
  notice: string | null;
  /** Set when the page is served from a hostname this build was not made for. */
  hostnameWarning: string | null;
  /** Address that signed in but that the server says may not page. */
  rejectedEmail: string | null;
  /** Drops the rejected session so another account can be chosen. */
  onUseAnotherAccount: () => void;
  /** True once Google's real button exists, so our overlay actually does something. */
  buttonReady: boolean;
}

/**
 * The rejection state says an account cannot page, and deliberately does NOT say
 * which domains could.
 *
 * The allowlist is server side only (functions/lib/domains.ts) and the verdict
 * comes from POST /api/session, so this component never holds the list. An earlier
 * version rendered the three allowed domains here, which shipped them in the public
 * bundle and handed anyone who signed in a phishing target list.
 */
export function LoginScreen({
  buttonRef,
  loading,
  blockedReason,
  notice,
  hostnameWarning,
  rejectedEmail,
  onUseAnotherAccount,
  buttonReady,
}: LoginScreenProps) {
  return (
    <main className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center gap-8 px-4 py-10 motion-safe:animate-fade-in sm:py-16">
      <div className="flex flex-col items-center gap-4 text-center motion-safe:animate-fade-in-up">
        <span className="flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 motion-safe:animate-breathe">
          <RescueIcon className="size-7" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Vertex Rescue</h1>
      </div>

      {/* Shown alongside whatever else is on screen: a wrong hostname is the
          reason sign in fails here, whatever the other symptom looks like. */}
      {hostnameWarning !== null && (
        <div className="rounded-lg border border-warning-border bg-warning-bg p-4 text-warning-fg motion-safe:animate-fade-in-up">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <p className="min-w-0 break-words text-sm">{hostnameWarning}</p>
          </div>
        </div>
      )}

      {rejectedEmail !== null ? (
        <div className="rounded-lg border border-warning-border bg-warning-bg p-4 text-warning-fg motion-safe:animate-fade-in-up">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="text-base font-bold">Esta cuenta no puede llamar a guardia</h2>
              <p className="mt-1 break-words text-sm">
                <span className="font-semibold">{rejectedEmail}</span> inició sesión, pero no tiene
                permiso para avisarle a guardia. Usá tu cuenta corporativa de Vertex.
              </p>
              <button
                type="button"
                onClick={onUseAnotherAccount}
                className="mt-3 inline-flex h-11 items-center justify-center rounded-lg border border-warning-border bg-background px-4 text-sm font-semibold text-warning-fg transition-colors hover:bg-warning-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-border focus-visible:ring-offset-2"
              >
                Usar otra cuenta
              </button>
            </div>
          </div>
        </div>
      ) : blockedReason !== null ? (
        <div className="rounded-lg border border-danger-border bg-danger-bg p-4 text-danger-fg motion-safe:animate-fade-in-up">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="text-base font-bold">El inicio de sesión no está disponible</h2>
              <p className="mt-1 break-words text-sm">{blockedReason}</p>
              <p className="mt-2 text-sm">
                Hasta que se resuelva, llamá por teléfono al ingeniero de guardia. Esta página no
                puede avisarle a nadie.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 motion-safe:animate-fade-in-up">
          {/* The Google button is rendered into this div by the GSI script, so the
              div must exist before the script is asked to draw anything. Keeping a
              fixed height stops the layout jumping when it appears. */}
          {/* Our button, with Google's real one invisible on top of it.
              
              Everything Google renders lives in a cross origin iframe we cannot
              style, and their white logo chip was the last piece that could not be
              themed to match a dark page. So they keep the click, we keep the
              pixels. Their branding guidelines allow a custom button provided it
              carries the real G mark and approved wording, which this does.
              
              The two must stay the same size: the invisible layer is the actual
              hit area, so any part of ours it does not cover would look clickable
              and do nothing. Both are GSI_BUTTON_WIDTH wide. */}
          <div className="relative" style={{ width: GSI_BUTTON_WIDTH, height: 44 }}>
            <button
              type="button"
              // Decoration. The real control is Google's button above it, which is
              // what receives focus and what a screen reader announces, so this
              // must not be a second tab stop announcing the same thing.
              aria-hidden="true"
              tabIndex={-1}
              className={[
                'pointer-events-none absolute inset-0 flex w-full items-center justify-center gap-3',
                'rounded-full border border-input bg-card text-sm font-semibold text-foreground',
                'shadow-sm transition-colors',
                buttonReady ? '' : 'opacity-60',
              ].join(' ')}
            >
              <GoogleMark />
              Acceder con Google
            </button>

            {/* Google's button. opacity-0, not hidden or display:none, because it
                still has to be laid out and hit tested to receive the click. */}
            <div ref={buttonRef} className="absolute inset-0 opacity-0 [color-scheme:normal]" />
          </div>
          {loading && (
            <p className="text-sm text-muted-foreground" role="status">
              Cargando el inicio de sesión de Google.
            </p>
          )}
          {notice !== null && (
            <p className="max-w-sm text-center text-sm font-medium text-warning-fg">{notice}</p>
          )}
        </div>
      )}
    </main>
  );
}
